/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { Formik } from 'formik';
import { withNamespaces } from 'react-i18next';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import compose from 'lodash/fp/compose';
import valuesIn from 'lodash/valuesIn';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import some from 'lodash/some';
import { Container, Column, Row } from '../../../../../styles/Grid';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { setDataLayerObj } from '../../../../../utils/analytics';
import { CheckboxButton } from '../../../../ui/Form/CheckboxButton';
import { withConsumer } from '../../../../../contexts';
import { SolutionBuilderConsumer } from '../../../../../contexts/SolutionBuilderContext';
import { SidePanel } from '../../SidePanel';
import * as commonStyles from '../../index.styles';
import * as styles from './index.styles';

const defaultData = {
  ProductHeadline: { value: '' },
  ProductSubtitle: { value: '' },
  ProductSidePanelCTA: { value: '' },
  OfficeSpaceCopy: { value: '' },
  BusinessAddressCopy: { value: '' },
  BusinessTelephoneCopy: { value: '' },
  ProductSelectMoreText: { value: '' },
  ProductsSidePanelHeadline: { value: '' },
  ProductsSidePanelBusinessAddress: { value: '' },
  ProductsSidePanelBusinessTelephone: { value: '' },
  ProductsSidePanelOfficeSpace: { value: '' },
};

const ANALYTICS_EVENT_NAME = 'sbQuestions';

export class SelectSolutions extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  state = {
    isSidePanelOpen: false,
  };

  componentDidMount() {
    const { manager } = this.props;
    const initialValues = manager.getStepData();

    manager.setHelpText(get(this.fields, 'ProductSelectMoreText.value', ''));
    this.toggleNextButton(initialValues);
    this.setAnalytics();
  }

  onFormSubmit = values => {
    const { manager } = this.props;
    manager.setStepData(values);
  };

  setAnalytics = () => {
    const dataLayerObject = {
      event: ANALYTICS_EVENT_NAME,
    };
    setDataLayerObj(dataLayerObject);
  };

  openSidePanel = () => {
    this.setState({ isSidePanelOpen: true });
  };

  closeSidePanel = () => {
    this.setState({ isSidePanelOpen: false });
  };

  renderHeadline() {
    const {
      ProductHeadline,
      ProductSubtitle,
      ProductSidePanelCTA,
    } = this.fields;

    return (
      <Column cols={{ sm: 6 }}>
        <h1 css={commonStyles.headline}>{get(ProductHeadline, 'value', '')}</h1>
        <p css={commonStyles.description}>
          {get(ProductSubtitle, 'value', '')}
        </p>
        <div css={commonStyles.secondaryCTAContainer}>
          <button
            onClick={this.openSidePanel}
            css={commonStyles.secondaryCTAButton}
          >
            {get(ProductSidePanelCTA, 'value', '')}
          </button>
        </div>
      </Column>
    );
  }

  renderSidePanel() {
    return (
      <SidePanel
        isSidePanelOpen={this.state.isSidePanelOpen}
        header={this.renderSidePanelHeading}
        body={this.renderSidePanelBody}
        onRequestClose={this.closeSidePanel}
        toggleSidePanel={this.closeSidePanel}
      />
    );
  }

  renderSidePanelHeading = () => {
    return this.fields.ProductsSidePanelHeadline.value;
  };

  renderSidePanelBody = () => {
    const {
      ProductsSidePanelOfficeSpace,
      ProductsSidePanelBusinessAddress,
      ProductsSidePanelBusinessTelephone,
    } = this.fields;

    return (
      <React.Fragment>
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={ProductsSidePanelOfficeSpace}
        />
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={ProductsSidePanelBusinessAddress}
        />
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={ProductsSidePanelBusinessTelephone}
        />
      </React.Fragment>
    );
  };

  showHelpMessage = () => {
    this.props.manager.showHelpMessage();
  };

  toggleNextButton = formValues => {
    const { manager } = this.props;
    const isNextEnabled = some(valuesIn(formValues), value => value === true);

    if (isNextEnabled) manager.enableNext();
    else manager.disableNext();
  };

  /**
   * Needs to be attached to `validate` Formik method as a hack since
   * there's no callback for a common onChange.
   * @see https://github.com/formium/formik/issues/1633
   */
  onFormInputChange = formValues => {
    this.toggleNextButton(formValues);
  };

  renderForm() {
    const { manager } = this.props;
    const initialValues = manager.getStepData();
    const {
      OfficeSpaceCopy,
      BusinessAddressCopy,
      BusinessTelephoneCopy,
    } = this.fields;

    return (
      <Formik
        onSubmit={this.onFormSubmit}
        initialValues={initialValues}
        validate={this.onFormInputChange}
        render={({ handleSubmit, values, handleChange, submitForm }) => {
          manager.setSubmitFormTrigger(submitForm);

          return (
            <form css={styles.form} onSubmit={handleSubmit}>
              <div css={styles.checkboxContainer}>
                <CheckboxButton
                  innerCss={{ container: styles.checkbox }}
                  id="officeSpace"
                  onChange={e => {
                    handleChange(e);
                    this.showHelpMessage();
                  }}
                  checked={values.officeSpace || false}
                >
                  <RichText field={OfficeSpaceCopy} />
                </CheckboxButton>
                <CheckboxButton
                  innerCss={{ container: styles.checkbox }}
                  id="businessAddress"
                  onChange={e => {
                    handleChange(e);
                    this.showHelpMessage();
                  }}
                  checked={values.businessAddress || false}
                >
                  <RichText field={BusinessAddressCopy} />
                </CheckboxButton>
                <CheckboxButton
                  innerCss={{ container: styles.checkbox }}
                  id="businessTelephoneNumber"
                  onChange={e => {
                    handleChange(e);
                    this.showHelpMessage();
                  }}
                  checked={values.businessTelephoneNumber || false}
                >
                  <RichText field={BusinessTelephoneCopy} />
                </CheckboxButton>
              </div>
            </form>
          );
        }}
      />
    );
  }

  safeRender() {
    return (
      <div css={commonStyles.container}>
        <Container>
          <Row>
            {this.renderHeadline()}
            {this.renderForm()}
            {this.renderSidePanel()}
          </Row>
        </Container>
      </div>
    );
  }
}

SelectSolutions.displayName = 'SelectSolutions';

SelectSolutions.defaultProps = {};

SelectSolutions.propTypes = {};

export default compose(
  withNamespaces(),
  withConsumer(SolutionBuilderConsumer, 'manager'),
)(SelectSolutions);
